import React from 'react';
import YouTube, { YouTubeEvent, YouTubeProps } from 'react-youtube';

interface YouTubePlayerProps {
  videoId: string;
  onReady: (player: YT.Player) => void;
  onError?: (error: any) => void;
  maximized?: boolean;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({ videoId, onReady, onError, maximized = false }) => {
  const opts: YouTubeProps['opts'] = {
    height: maximized ? '100%' : '180',
    width: maximized ? '100%' : '320',
    playerVars: {
      // Start paused, allow JS to control playback
      autoplay: 0,
      // Hide native controls — playback is driven by the app
      controls: 0,
      // Disable keyboard controls
      disablekb: 1,
      // Disable related videos at the end
      rel: 0,
      // Modest branding
      modestbranding: 1,
    },
  };

  const handleReady = (event: YouTubeEvent) => {
    const player = event.target;
    // Best-effort hint — YouTube may ignore this and pick its own quality.
    // Start at the lowest tier while idle; the engine bumps to 'highres' on play.
    try {
      player.setPlaybackQuality?.('small');
    } catch {
      /* ignore */
    }
    // To avoid both YouTube posters (the initial red play button and the
    // pause-state centred arrow), keep the player silently playing whenever
    // the app isn't actively driving it. The engine mutes/un-mutes around
    // play/stop.
    try {
      player.mute?.();
      player.setPlaybackRate?.(0.25);
      player.playVideo?.();
    } catch {
      /* ignore */
    }
    onReady(player);
  };

  const handleError = (event: YouTubeEvent) => {
    if (onError) {
      onError(event.data);
    }
  };

  return (
    <div
      className="youtube-player-container"
      style={
        maximized
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1050,
              background: '#000',
            }
          : { position: 'relative', display: 'inline-block' }
      }
    >
      <YouTube
        videoId={videoId}
        opts={opts}
        onReady={handleReady}
        onError={handleError}
        style={maximized ? { width: '100%', height: '100%' } : undefined}
        iframeClassName={maximized ? 'w-100 h-100' : undefined}
      />
      {/* Transparent overlay blocks all mouse interaction with the iframe */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          cursor: 'not-allowed',
        }}
      />
    </div>
  );
};

export default YouTubePlayer;
